import React, { useState, useEffect, useCallback, useContext } from 'react';
import RecommendedCard from './RecommendedCard';
import Search from '../../CommonComponents/Search';
import IconButton from '../../CommonComponents/IconButton';
import useAxiosPrivate from '../../../Hooks/useAxiosPrivate';
import Loading from '../../Notification-Loading/Loading/Loading';
import { useDebouncedValue } from '../../../Hooks/useDebounceValue';
import FilterDropdown from '../../CommonComponents/FilterDropdown';
import AlertContext from "../../../context/AlertContext";
import constants from "../../../constants";

const Recommended = () => {
  const {
    setShowNotify,
  } = useContext(AlertContext);

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1); // Current page
  const [pageSize] = useState(10); // Number of items per page
  const [hasMore, setHasMore] = useState(true);
  const [searchValue, setSearchValue] = useState(''); // Search input state
  const debouncedSearchValue = useDebouncedValue(searchValue, 2000); // Debounced search value
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [totalCandidatesFetched, setTotalCandidatesFetched] = useState(false);
  const [shortlistedRoles, setShortlistedRoles] = useState([]); // State for shortlisted roles
  const [selectedRole, setSelectedRole] = useState("");
  const axios = useAxiosPrivate();


  const fetchCandidates = useCallback(async () => {
    if (!hasMore) return;

    try {
      setLoading(true);
      const response = await axios.get(
        `node/admin/shortlisted/candidates?page=${page}&pageSize=${pageSize}&name=${debouncedSearchValue}&role=${selectedRole}`,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.data.resultCode === constants.RESULT_STATUS.SUCCESS) {
        const newCandidates = response.data.data;
        const totalCount = response.data.totalCount;
        // if (!totalCandidatesFetched) {
        setTotalCandidates(totalCount); // Set total candidates only once
        setTotalCandidatesFetched(true); // Set the flag to true to avoid re-fetching
        // }
        if (newCandidates.length < pageSize) {
          setHasMore(false); // No more data to fetch
        }
        setCandidates((prevCandidates) => [
          ...prevCandidates,
          ...newCandidates,
        ]);
      } else {
        setShowNotify({
          show: true,
          title: "Error!",
          msg: response.data.msg || "Something went wrong. Please try again later or contact support.",
        });
      }
    } catch (err) {
      // console.error("Error fetching questions:", err);
      setShowNotify({
        show: true,
        title: err.message.includes("403") ? "Warning!" : "Error!",
        msg: err.message.includes("403")
          ? "You have been logged-out due to inactivity. Login again."
          : "Something went wrong. Please try again later or contact support.",
        isUnAuth: err.message.includes("403"),
      });
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [axios, page, debouncedSearchValue, setShowNotify, pageSize, hasMore, selectedRole]);



  const fetchShortlistedRoles = useCallback(async () => {
    try {
      const response = await axios.get('node/admin/shortlisted/candidates/role', {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.data.resultCode === constants.RESULT_STATUS.SUCCESS) {
        const data = response.data.data;
        setShortlistedRoles(data);
        console.log('Shortlisted Roles:', data);
      } else {
        setShowNotify({
          show: true,
          title: "Error!",
          msg: response.data.msg || "Something went wrong. Please try again later or contact support.",
        });
      }
    } catch (error) {
      console.error('Error fetching shortlisted roles:', error);
      setShowNotify({
        show: true,
        title: error.message.includes("403") ? "Warning!" : "Error!",
        msg: error.message.includes("403")
          ? "You have been logged-out due to inactivity. Login again."
          : "Something went wrong. Please try again later or contact support.",
        isUnAuth: error.message.includes("403"),
      });
    }
  }, [axios, setShowNotify]);

  const handleSelect = (item) => {
    console.log("Selected item:", item.label);
    setSelectedRole(item.label);
  };


  useEffect(() => {
    setPage(1)
    setHasMore(true)
    setCandidates([])
  }, [debouncedSearchValue, selectedRole])

  useEffect(() => {
    fetchCandidates();
    fetchShortlistedRoles();
  }, [debouncedSearchValue, page, fetchCandidates, fetchShortlistedRoles]);

  const handleScroll = useCallback(() => {
    if (loading || !hasMore) return; // Prevent trigger while loading or fetching

    const bottom =
      window.innerHeight + document.documentElement.scrollTop >=
      document.documentElement.offsetHeight;

    if (bottom) {
      setPage((prevPage) => prevPage + 1); // Scroll reached bottom, load more
    }
  }, [loading, hasMore]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <div className="recommendedMainContainer">
      <div className="recommendedHeadingSection">
        <div className="recommendedHeadingLeftSection">
          <p>Shortlisted Candidates <span className='totalCandidatesCount'>({totalCandidates})</span></p>
        </div>

        <div className="recommendedHeadingRightSection">
          {/* <IconButton hierarchy={"secondaryGrey"}>
            <svg
              width="20"
              height="20"
              fill="none"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke="#667085"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 10H15M2.5 5H17.5M7.5 15H12.5"
              />
            </svg>
          </IconButton> */}
          <FilterDropdown
            icon={
              <svg
                width="20"
                height="20"
                fill="none"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke="#667085"
                  strokeWidth="1.66667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 10H15M2.5 5H17.5M7.5 15H12.5"
                />
              </svg>
            }
            menuItems={shortlistedRoles}
            onSelect={handleSelect}
            supportingText={true}
          />
          <Search
            iconPosition="end"
            placeholder="Search Student"
            width={"232px"}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)} // Update search value
            onSearch={() => setPage(1)} // Reset page when search is triggered
          />
        </div>
      </div>

      <div className="recommendedCardsContainer">
        {candidates.map((candidate) => {
          let educationDetails = {};
          try {
            educationDetails = candidate.education !==null && candidate.education !== undefined ? JSON.parse(candidate.education) : {};
          } catch (e) {
            console.error("Error parsing education details:", e);
          }

          return (
            <RecommendedCard
              key={candidate.id}
              lid={candidate.id}
              bid={candidate.batchid}
              name={candidate.name.trim()}
              graduation={`${educationDetails?.branch || "Unknown Branch"}`}
              img={candidate.picture}
              scores={Object.values(candidate.skillDetails || {})}
              recommendedFor={candidate.roletitle}
            />
          );
        })}


        {error && <p>{error}</p>}
      </div>
      {loading && <Loading overlay/>}
    </div>
  );
}

export default Recommended;